import * as React from "react";
import { Loader2, AlertCircle } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@eduviaos/ui-kit";
import { ApiError } from "@eduviaos/shared";
import { usePreviewTemplate } from "@/lib/emailTemplates/api";

interface LivePreviewPaneProps {
  templateKey: string;
  subject: string;
  html_body: string;
  text_body: string;
  /** ms — debounce window for re-rendering. 400ms balances feel + load. */
  debounceMs?: number;
}

/**
 * Server-side rendered preview. Sandboxed iframe isolates HTML execution
 * so the SA's draft can't escape into the editor app. Plain-text view
 * uses a `<pre>` so whitespace renders honestly.
 */
export function LivePreviewPane({
  templateKey,
  subject,
  html_body,
  text_body,
  debounceMs = 400,
}: LivePreviewPaneProps) {
  const previewMutation = usePreviewTemplate(templateKey);
  const [renderedHtml, setRenderedHtml] = React.useState<string>("");
  const [renderedText, setRenderedText] = React.useState<string>("");
  const [renderedSubject, setRenderedSubject] = React.useState<string>(subject);
  const [error, setError] = React.useState<string | null>(null);
  const inflightToken = React.useRef(0);

  React.useEffect(() => {
    const handle = window.setTimeout(async () => {
      const myToken = ++inflightToken.current;
      try {
        const result = await previewMutation.mutateAsync({
          subject,
          html_body,
          text_body,
        });
        // Drop stale responses if a newer keystroke has fired.
        if (myToken !== inflightToken.current) return;
        setRenderedHtml(result.html_rendered);
        setRenderedText(result.text_rendered);
        setRenderedSubject(result.subject_rendered);
        setError(null);
      } catch (err) {
        if (myToken !== inflightToken.current) return;
        if (err instanceof ApiError) {
          setError(err.message || "Could not render preview.");
        } else {
          setError("Could not render preview.");
        }
      }
    }, debounceMs);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subject, html_body, text_body, templateKey, debounceMs]);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex flex-row items-center justify-between gap-2 border-b pb-3">
        <CardTitle className="text-sm font-semibold">Live preview</CardTitle>
        {previewMutation.isPending && (
          <Loader2 className="text-muted-foreground size-3.5 animate-spin" aria-hidden="true" />
        )}
      </CardHeader>
      <CardContent className="grid gap-3 p-4">
        <div className="grid gap-1">
          <p className="text-muted-foreground text-[11px] font-medium uppercase tracking-wide">
            Subject
          </p>
          <p className="font-medium leading-snug">{renderedSubject || "—"}</p>
        </div>
        {error ? (
          <div
            role="alert"
            className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2.5 text-xs"
          >
            <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </div>
        ) : (
          <Tabs defaultValue="html">
            <TabsList>
              <TabsTrigger value="html">HTML</TabsTrigger>
              <TabsTrigger value="text">Plain text</TabsTrigger>
            </TabsList>
            <TabsContent value="html" className="mt-3">
              <iframe
                title="Email HTML preview"
                sandbox=""
                srcDoc={renderedHtml}
                className="h-[420px] w-full rounded-md border bg-white"
              />
            </TabsContent>
            <TabsContent value="text" className="mt-3">
              <pre className="bg-muted/30 h-[420px] overflow-auto whitespace-pre-wrap rounded-md border p-4 font-mono text-xs leading-relaxed">
                {renderedText}
              </pre>
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
    </Card>
  );
}
